import usePCD from '@/composables/annotationTool/usePCD.js';

export default function useProjection() {
  const {
    getPointsColorByXValue,
  } = usePCD();

  const zxydwh2xyzmin = [
    [0, 0, 1, 0, 0, -0.5, 0],
    [0, 0, 0, 1, 0, 0, -0.5],
    [0, 1, 0, 0, 0, 0, 0],
  ];

  const zxydwh2xyzmax = [
    [0, 0, 1, 0, 0, 0.5, 0],
    [0, 0, 0, 1, 0, 0, 0.5],
    [0, 1, 0, 0, 0, 0, 0],
  ];

  const coordinateTransform = [
    [-1, 0, 1],
    [0, -1, 1],
    [0, 0, 1],
  ];

  function transpose(matrix) {
    return matrix[0].map((col, c) => matrix.map((row, r) => matrix[r][c]));
  }

  function multRow(a, r, m) {
    const nr = a.length; const
      nc = a[0].length;
    for (let c = 0; c < nc; c++) {
      a[r][c] = a[r][c] * m;
    }
    return a;
  }

  function normalizeByLastRow(a) {
    const nr = a.length; const
      nc = a[0].length;
    for (let r = 0; r < nr; r++) {
      for (let c = 0; c < nc; c++) {
        a[r][c] = a[r][c] / a[nr - 1][c];
      }
    }
    return a;
  }

  function maximum(a, b) {
    const d = [];
    const nr = a.length; const
      nc = a[0].length;
    for (let r = 0; r < nr; r++) {
      d[r] = [];
      for (let c = 0; c < nc; c++) {
        d[r][c] = Math.max(a[r][c], b[r][c]);
      }
    }
    return d;
  }

  function minimum(a, b) {
    const nr = a.length; const
      nc = a[0].length;
    const d = [];
    for (let r = 0; r < nr; r++) {
      d[r] = [];
      for (let c = 0; c < nc; c++) {
        d[r][c] = Math.min(a[r][c], b[r][c]);
      }
    }
    return d;
  }

  function subtract(a, b) {
    const nr = a.length; const
      nc = a[0].length;
    const d = [];
    for (let r = 0; r < nr; r++) {
      d[r] = [];
      for (let c = 0; c < nc; c++) {
        d[r][c] = a[r][c] - b[r][c];
      }
    }
    return d;
  }

  function dropLastRow(a) {
    const nr = a.length; const
      nc = a[0].length;
    const b = [];
    for (let r = 0; r < nr - 1; r++) {
      b[r] = [];
      for (let c = 0; c < nc; c++) {
        b[r][c] = a[r][c];
      }
    }
    return b;
  }

  function multiply(a, b) {
    const aNumRows = a.length; const aNumCols = a[0].length;
    const bNumRows = b.length; const bNumCols = b[0].length;
    const m = new Array(aNumRows); // initialize array of rows
    for (let r = 0; r < aNumRows; ++r) {
      m[r] = new Array(bNumCols); // initialize the current row
      for (let c = 0; c < bNumCols; ++c) {
        m[r][c] = 0; // initialize the current cell
        for (let i = 0; i < aNumCols; ++i) {
          m[r][c] += a[r][i] * b[i][c];
        }
      }
    }
    return m;
  }

  function convert3d2d(bb3d, intrinsic) {
    const [fx, fy, cx, cy] = intrinsic;

    const height = cy * 2;
    const width = cx * 2;

    // Camera matrix that produces normalized bounding boxes.
    const cameraMatrix = [
      [fx / width, 0, cx / width],
      [0, fy / height, cy / height],
      [0, 0, 1],
    ];

    const xyzmin = multiply(zxydwh2xyzmin, transpose(bb3d));
    let bbmin = multiply(cameraMatrix, xyzmin);
    bbmin = normalizeByLastRow(bbmin);
    bbmin = multiply(coordinateTransform, bbmin);
    bbmin = dropLastRow(bbmin);
    bbmin = multRow(bbmin, 0, width);
    bbmin = multRow(bbmin, 1, height);

    const xyzmax = multiply(zxydwh2xyzmax, transpose(bb3d));
    let bbmax = multiply(cameraMatrix, xyzmax);
    bbmax = normalizeByLastRow(bbmax);
    bbmax = multiply(coordinateTransform, bbmax);
    bbmax = dropLastRow(bbmax);
    bbmax = multRow(bbmax, 0, width);
    bbmax = multRow(bbmax, 1, height);

    const bbmax_ = maximum(bbmax, bbmin);
    bbmin = minimum(bbmax, bbmin);
    let sizes = subtract(bbmax_, bbmin);

    bbmin = transpose(bbmin);
    sizes = transpose(sizes);

    const boxes = [];
    for (let k = 0; k < bbmin.length; k++) {
      const b2 = {
        x: bbmin[k][0],
        y: bbmin[k][1],
        width: sizes[k][0],
        height: sizes[k][1],
      };

      boxes.push(b2);
    }

    return boxes;
  }

  // Assuming your intrinsic parameters are like: [fx, fy, cx, cy]
  async function drawPCDPoints(
    pcdPoints,
    image_width,
    image_height,
    options = {},
  ) {
    if (!pcdPoints || !image_width || !image_height) return null;

    const {
      radius = 2,
      camera_fx = 1240.31,
      camera_fy = 1232.88,
    } = options;

    const geometry = pcdPoints.geometry;
    const positionAttribute = geometry.getAttribute('position');

    const colors = getPointsColorByXValue(positionAttribute);

    // Extract the 3D points (position array) from the BufferGeometry.
    const points = [];
    const filteredColors = [];
    for (let i = 0; i < positionAttribute.count; i++) {
      const x = positionAttribute.getX(i);
      const y = positionAttribute.getY(i);
      const z = positionAttribute.getZ(i);

      if (x >= 0) {
        points.push([0, x, y, z, 0.5, 0.5, 0.5]);
        filteredColors.push(colors[i]); // Keep color for valid points
      }
    }

    // Assuming your intrinsic parameters are like: [fx, fy, cx, cy]
    // Calculate intrinsic parameters for camera projection (as in the Python code)
    const cameraIntrinsic = [camera_fx, camera_fy, image_width / 2, image_height / 2]; // Replace with actual values

    // Convert all 3d points to 2d
    const convertedPoints = convert3d2d(points, cameraIntrinsic);

    // Get 2d point centers
    const centers_2d = convertedPoints.map((point) => ({
      x: point.x + (point.width / 2),
      y: point.y + (point.height / 2),
    }));

    // Draw the LiDAR image
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    canvas.width = image_width;
    canvas.height = image_height;
    ctx.clearRect(0, 0, image_width, image_height);
    centers_2d.forEach((center, i) => {
      const r = Math.floor(filteredColors[i].r * 255);
      const g = Math.floor(filteredColors[i].g * 255);
      const b = Math.floor(filteredColors[i].b * 255);
      ctx.beginPath();
      ctx.arc(center.x, center.y, radius, 0, 2 * Math.PI);
      ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;
      ctx.fill();
    });

    // Create and load the image asynchronously
    const img = new Image();
    const loadImage = new Promise((resolve) => {
      img.onload = () => resolve(img);
      img.src = canvas.toDataURL();
    });

    // Wait for the image to load
    const loadedImg = await loadImage;
    return loadedImg;
  }

  return {
    drawPCDPoints,
  };
}
